/**
 * Configul pietei, disponibil oriunde in arbore.
 *
 * Textele vin din `config.texts[locale]`, dar fiecare apel cere si un text de
 * rezerva in romana. Nu exista varianta "cheie lipsa => se afiseaza cheia":
 * o piata careia nu i s-a tradus un buton trebuie sa arate un buton cu text
 * corect, nu `checkout.confirm_button`.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import type { BootstrapResponse, PublicMarketConfig } from "@deliveryapp/shared";

export interface BootstrapContextValue {
  marketKey: string;
  bootstrap: BootstrapResponse;
  config: PublicMarketConfig;
  locale: string;
  availableLocales: string[];
  setLocale: (locale: string) => void;
  t: (key: string, fallback: string) => string;
}

const BootstrapContext = createContext<BootstrapContextValue | null>(null);

const LOCALE_STORAGE_PREFIX = "deliveryapp.locale.v1";

function readStoredLocale(marketKey: string): string | null {
  try {
    return window.localStorage.getItem(`${LOCALE_STORAGE_PREFIX}.${marketKey}`);
  } catch {
    // Modul privat sau stocare plina: limba nu se retine intre vizite, restul
    // aplicatiei functioneaza. Nu e o eroare de aratat clientului.
    return null;
  }
}

export function BootstrapProvider({
  marketKey,
  bootstrap,
  children,
}: {
  marketKey: string;
  bootstrap: BootstrapResponse;
  children: ReactNode;
}) {
  const config = bootstrap.config;
  const availableLocales = useMemo(() => {
    const declared = config.identity.locales.filter((item) => typeof item === "string" && item.length > 0);
    return declared.length > 0 ? declared : [config.identity.defaultLocale];
  }, [config.identity.locales, config.identity.defaultLocale]);

  const [locale, setLocaleState] = useState<string>(() => {
    const stored = readStoredLocale(marketKey);
    if (stored !== null && availableLocales.includes(stored)) return stored;
    return config.identity.defaultLocale;
  });

  const setLocale = useCallback(
    (next: string) => {
      if (!availableLocales.includes(next)) return;
      setLocaleState(next);
      try {
        window.localStorage.setItem(`${LOCALE_STORAGE_PREFIX}.${marketKey}`, next);
      } catch {
        return;
      }
    },
    [availableLocales, marketKey],
  );

  // `lang` urmareste limba ACTIVA, nu pe cea implicita a pietei. Se scrie aici,
  // nu in `App`, pentru ca efectul parintelui ruleaza dupa cel al copilului si ar
  // suprascrie alegerea clientului imediat dupa ce a facut-o.
  useEffect(() => {
    document.documentElement.lang = locale || config.identity.defaultLocale || "ro";
  }, [locale, config.identity.defaultLocale]);

  const t = useCallback(
    (key: string, fallback: string): string => {
      const inLocale = config.texts?.[locale]?.[key];
      if (typeof inLocale === "string" && inLocale.length > 0) return inLocale;
      const inDefault = config.texts?.[config.identity.defaultLocale]?.[key];
      if (typeof inDefault === "string" && inDefault.length > 0) return inDefault;
      return fallback;
    },
    [config.texts, config.identity.defaultLocale, locale],
  );

  const value = useMemo<BootstrapContextValue>(
    () => ({ marketKey, bootstrap, config, locale, availableLocales, setLocale, t }),
    [marketKey, bootstrap, config, locale, availableLocales, setLocale, t],
  );

  return <BootstrapContext.Provider value={value}>{children}</BootstrapContext.Provider>;
}

export function useBootstrap(): BootstrapContextValue {
  const value = useContext(BootstrapContext);
  if (value === null) {
    throw new Error("useBootstrap a fost apelat in afara lui BootstrapProvider.");
  }
  return value;
}

export function useConfig(): PublicMarketConfig {
  return useBootstrap().config;
}

export function useText(): (key: string, fallback: string) => string {
  return useBootstrap().t;
}
